import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { CompanySuspendedMask } from '@/companies/components';
import {
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
    useParams
} from 'react-router';
import { Toaster } from 'react-hot-toast';
import { LoginPage } from './pages/login';
import {
    NullableTokensProvider,
    TokensProvider,
    useNullableTokens,
    useTokens
} from './common/providers/tokens-provider';
import { SignupPage } from './pages/signup';
import { ForgotPasswordPage } from './pages/forgot-password';
import { ResetPasswordPage } from './pages/reset-password';
import { NotFoundPage } from './pages/not-found';
import { OnboardPage } from './pages/onboard';
import { MainPage } from './pages/main';
import { DashboardPage } from './pages/dashboard';
import { TaskIdPage, TasksPage } from './pages/tasks';
import {
    BeaconsSettingsPage,
    BillingSettingsPage,
    DeviceIdSettingsPage,
    DevicesSettingsPage,
    GeneralSettingsPage,
    MemberIdAppDevicesSettingsPage,
    MemberIdGeneralSettingsPage,
    MemberIdNotificationsSettingsPage,
    MemberIdEmergencyInformationSettingsPage,
    MemberIdSettingsPage,
    MembersSettingsPage,
    SettingsPage,
    TeamIdDevicesSettingsPage,
    TeamIdGeneralSettingsPage,
    TeamIdMembersSettingsPage,
    TeamIdNotificationsSettingsPage,
    TeamIdSettingsPage,
    TeamsSettingsPage
} from './pages/settings';
import { ReportsPage } from './pages/reports';
import {
    TaskDashboardProvider,
    TaskFilterProvider,
    TasksProvider
} from './tasks/providers';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
    UIButton,
    UIConfirmationModalProvider,
    UINavProvider,
    UISpinner
} from './ui';
import { RegionProvider, useRegion } from './common/providers/region-provider';
import {
    AccountPage,
    AccountDetailsSettingsPage,
    SecuritySettingsPage
} from './pages/account';
import { DevicePage } from './pages/device';
import { EmailSettingsPage } from './pages/account/email-settings-page';
import { VerifyEmailPage } from './pages/verify-email';
import {
    OpenApiProvider,
    useOpenApi
} from './common/providers/open-api-provider';
import { VerifyNewEmailPage } from './pages/verify-new-email';
import { SsoCallbackPage } from './pages/sso-callback';
import { PhoneSettingsPage } from './pages/account/phone-settings-page';
import {
    ReportsFilterProvider,
    TasksAndAlertsAnalyticsProvider
} from './analytics/providers';
import { EmergencyBroadcastPage } from './pages/emergency-broadcast';
import { useIntercom } from './common/util/intercom/intercom';
import { Device, Invite } from '@/api';
import styles from './app.module.scss';
import { PreferencesProvider } from './common/providers/preferences-provider';
import { WebsocketProvider } from './common/providers/websocket-provider';
import { useAuthenticatedApi } from './common/hooks/use-authenticated-api';
import { useUnauthenticatedApi } from './common/hooks/use-unauthenticated-api';
import { MeProvider } from './me/providers';
import { useFetchMe } from './me/queries';
import { useTimezoneAutoDetect } from './me/hooks';
import { useFetchCompanies, useFetchInvites } from './companies/queries';
import {
    CompaniesProvider,
    CompanyContextProvider,
    InvitesProvider,
    MyCompanyUsersProvider,
    NullableCompanyContextProvider,
    useCompanies,
    useCompanyContext,
    useNullableCompanyContext
} from './companies/providers';
import { useFetchDevices } from './devices/queries';
import { DevicesProvider } from './devices/providers';
import { EmergencyInformationSettingsPage } from './pages/account/emergency-information-settings-page';

const queryClient = new QueryClient();

function App(): ReactElement {
    return (
        <>
            <UIConfirmationModalProvider>
                <QueryClientProvider client={queryClient}>
                    <NullableTokensProvider>
                        <RegionProvider>
                            <App2 />
                        </RegionProvider>
                    </NullableTokensProvider>
                </QueryClientProvider>
            </UIConfirmationModalProvider>
            <Toaster />
        </>
    );
}

function App2(): ReactElement {
    useIntercom();

    return (
        <Routes>
            <Route path="/" element={<AppIndexRedirect />} />

            <Route element={<AppUnauthInit />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="signup" element={<SignupPage />} />
                <Route path="register" element={<SignupPage />} />
                <Route
                    path="forgot-password"
                    element={<ForgotPasswordPage />}
                />
                <Route
                    path="reset-password/:token"
                    element={<ResetPasswordPage />}
                />
            </Route>

            <Route
                path="verify-email/:token"
                element={
                    <OpenApiProvider>
                        <VerifyEmailPage />
                    </OpenApiProvider>
                }
            />
            <Route
                path="verify-new-email/:token"
                element={
                    <OpenApiProvider>
                        <VerifyNewEmailPage />
                    </OpenApiProvider>
                }
            />

            {/*
                The SSO popup lands here. It must stay outside AppUnauthInit,
                which redirects away as soon as tokens exist and would break the
                handshake.
            */}
            <Route path="sso-callback" element={<SsoCallbackPage />} />

            <Route path="/not-found" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/not-found" />} />

            <Route element={<AppAuthInit />}>
                {/* Not in company */}
                <Route
                    path="onboard"
                    element={
                        <AppOnboardInit>
                            <OnboardPage />
                        </AppOnboardInit>
                    }
                />

                <Route path="device" element={<DevicePage />} />

                {/* In company */}
                <Route
                    element={
                        <AppCompanyInit>
                            <TaskFilterProvider>
                                <UINavProvider>
                                    <MainPage />
                                </UINavProvider>
                            </TaskFilterProvider>
                        </AppCompanyInit>
                    }
                >
                    <Route path="company" element={<AppCompanyRedirect />} />
                    <Route
                        path="company/:companyId"
                        element={<AppCompanyRedirect />}
                    >
                        <Route
                            index
                            element={<Navigate to="dashboard" replace />}
                        />
                        <Route
                            path="dashboard"
                            element={
                                <TaskDashboardProvider>
                                    <DashboardPage />
                                </TaskDashboardProvider>
                            }
                        />
                        <Route
                            path="tasks"
                            element={
                                <TasksProvider>
                                    <TasksPage />
                                </TasksProvider>
                            }
                        />
                        <Route path="tasks/:taskId" element={<TaskIdPage />} />
                        <Route
                            path="reports"
                            element={
                                <ReportsFilterProvider>
                                    <TasksAndAlertsAnalyticsProvider>
                                        <ReportsPage />
                                    </TasksAndAlertsAnalyticsProvider>
                                </ReportsFilterProvider>
                            }
                        />
                        <Route
                            path="emergency-broadcast"
                            element={<EmergencyBroadcastPage />}
                        />
                        <Route path="settings" element={<SettingsPage />}>
                            <Route index element={<GeneralSettingsPage />} />
                            <Route
                                path="general"
                                element={<GeneralSettingsPage />}
                            />
                            <Route
                                path="billing"
                                element={<BillingSettingsPage />}
                            />
                            <Route
                                path="teams"
                                element={<TeamsSettingsPage />}
                            />
                            <Route
                                path="teams/:teamId"
                                element={<TeamIdSettingsPage />}
                            >
                                <Route
                                    index
                                    element={<TeamIdGeneralSettingsPage />}
                                />
                                <Route
                                    path="general"
                                    element={<TeamIdGeneralSettingsPage />}
                                />
                                <Route
                                    path="members"
                                    element={<TeamIdMembersSettingsPage />}
                                />
                                <Route
                                    path="devices"
                                    element={<TeamIdDevicesSettingsPage />}
                                />
                                <Route
                                    path="notifications"
                                    element={
                                        <TeamIdNotificationsSettingsPage />
                                    }
                                />
                            </Route>
                            <Route
                                path="members"
                                element={<MembersSettingsPage />}
                            />
                            <Route
                                path="members/:memberId"
                                element={<MemberIdSettingsPage />}
                            >
                                <Route
                                    index
                                    element={<MemberIdGeneralSettingsPage />}
                                />
                                <Route
                                    path="general"
                                    element={<MemberIdGeneralSettingsPage />}
                                />
                                <Route
                                    path="notifications"
                                    element={
                                        <MemberIdNotificationsSettingsPage />
                                    }
                                />
                                <Route
                                    path="emergency-information"
                                    element={
                                        <MemberIdEmergencyInformationSettingsPage />
                                    }
                                />
                                <Route
                                    path="app-devices"
                                    element={<MemberIdAppDevicesSettingsPage />}
                                />
                            </Route>
                            <Route
                                path="devices"
                                element={<DevicesSettingsPage />}
                            />
                            <Route
                                path="devices/:deviceId"
                                element={<DeviceIdSettingsPage />}
                            />
                            {/* Team-scoped device page: same page as above, but
                                reached from a team's Devices tab so its
                                breadcrumb stays within the team (a team admin
                                has no access to the company-wide devices list).
                                Declared flat here (not under teams/:teamId) so
                                it renders standalone without the team tab bar. */}
                            <Route
                                path="teams/:teamId/devices/:deviceId"
                                element={<DeviceIdSettingsPage />}
                            />
                            <Route
                                path="beacons"
                                element={<BeaconsSettingsPage />}
                            />
                        </Route>
                    </Route>
                    <Route path="account" element={<AccountPage />}>
                        <Route index element={<AccountDetailsSettingsPage />} />
                        <Route
                            path="details"
                            element={<AccountDetailsSettingsPage />}
                        />
                        <Route path="email" element={<EmailSettingsPage />} />
                        <Route
                            path="emergency-information"
                            element={<EmergencyInformationSettingsPage />}
                        />
                        <Route path="phone" element={<PhoneSettingsPage />} />
                        <Route
                            path="security"
                            element={<SecuritySettingsPage />}
                        />
                    </Route>
                </Route>
            </Route>
        </Routes>
    );
}

/*
 * The app shell below: the gates and redirects that decide which subtree of the
 * routing table above renders. None of them is a screen a user interacts with —
 * they are how the shell is drawn, so they live with it.
 */

/** Sends a visitor to their company or to the login page. */
function AppIndexRedirect(): ReactElement {
    const { tokens } = useNullableTokens();

    if (tokens) {
        return <Navigate to="/company" replace />;
    } else {
        return <Navigate to="/login" replace />;
    }
}

/**
 * The unauthenticated routes. Anyone already holding tokens is sent to the app,
 * so a signed-in user cannot land back on the login page.
 */
function AppUnauthInit(): ReactElement {
    return (
        <OpenApiProvider>
            <AppUnauthInit2 />
        </OpenApiProvider>
    );
}

function AppUnauthInit2(): ReactElement {
    const { tokens } = useNullableTokens();
    const { config } = useRegion();

    const { openApi } = useOpenApi();
    useUnauthenticatedApi({
        baseUrl: config.apiBaseUrl,
        axiosInstance: openApi.instance
    });

    if (tokens) {
        return <Navigate to="/" replace />;
    }

    return <Outlet />;
}

/**
 * The authenticated routes. Holds everything the app cannot start without — the
 * signed-in user, their companies, devices and invites — and shows the loading
 * or error screen until all four have arrived.
 */
function AppAuthInit(): ReactElement {
    const { tokens } = useNullableTokens();
    const location = useLocation();

    if (!tokens) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return (
        <TokensProvider tokens={tokens}>
            <OpenApiProvider>
                <WebsocketProvider>
                    <PreferencesProvider>
                        <AppAuthInit2 />
                    </PreferencesProvider>
                </WebsocketProvider>
            </OpenApiProvider>
        </TokensProvider>
    );
}

function AppAuthInit2(): ReactElement {
    const { tokens } = useTokens();
    const { config } = useRegion();

    const { openApi } = useOpenApi();

    useAuthenticatedApi({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        refreshUrl: 'v3/auth/refresh',
        baseUrl: config.apiBaseUrl,
        axiosInstance: openApi.instance
    });

    // Fake loading to make sure the loading screen doesn't "flicker" too quickly.
    // So this loading screen will last for at least 1 second.
    const [fakeLoading, setFakeLoading] = useState(true);
    useEffect(() => {
        if (fakeLoading) {
            setTimeout(() => {
                setFakeLoading(false);
            }, 1000);
        }
    }, [fakeLoading]);

    const fetchMe = useFetchMe();
    const fetchCompanies = useFetchCompanies();
    const fetchDevices = useFetchDevices();
    const fetchInvites = useFetchInvites();

    const refetch = () => {
        setFakeLoading(true);

        fetchMe.refetch();
        fetchCompanies.refetch();
        fetchDevices.refetch();
        fetchInvites.refetch();
    };

    const isLoading = useMemo(() => {
        return (
            fetchMe.status === 'pending' ||
            fetchCompanies.status === 'pending' ||
            fetchDevices.status === 'pending' ||
            fetchInvites.status === 'pending'
        );
    }, [
        fetchMe.status,
        fetchCompanies.status,
        fetchDevices.status,
        fetchInvites.status
    ]);

    const hasError = useMemo(() => {
        return (
            fetchMe.status === 'error' ||
            fetchCompanies.status === 'error' ||
            fetchDevices.status === 'error' ||
            fetchInvites.status === 'error'
        );
    }, [
        fetchMe.status,
        fetchCompanies.status,
        fetchDevices.status,
        fetchInvites.status
    ]);

    if (fakeLoading || isLoading) {
        return <AppLoading />;
    }

    if (hasError) {
        return <AppLoadingError onRetryClick={refetch} />;
    }

    return (
        <MeProvider
            me={{
                id: fetchMe.data!.user.id,
                email: fetchMe.data!.user.email
            }}
        >
            <CompaniesProvider companies={fetchCompanies.companies ?? []}>
                {/* Above the company context: it has to answer for any company,
                    including before one has been selected. */}
                <MyCompanyUsersProvider myUsers={fetchCompanies.myUsers ?? []}>
                    <NullableCompanyContextProvider>
                        <AppAuthInit3
                            devices={fetchDevices.devices ?? []}
                            invites={fetchInvites.invites ?? []}
                        />
                    </NullableCompanyContextProvider>
                </MyCompanyUsersProvider>
            </CompaniesProvider>
        </MeProvider>
    );
}

function AppAuthInit3(props: {
    devices: Device[];
    invites: Invite[];
}): ReactElement {
    const { devices, invites } = props;

    return (
        <DevicesProvider devices={devices}>
            <InvitesProvider invites={invites}>
                <AppAuthInit4 />
            </InvitesProvider>
        </DevicesProvider>
    );
}

function AppAuthInit4(): ReactElement {
    useTimezoneAutoDetect();

    return <Outlet />;
}

/** Onboarding is only reachable while the user has no company yet. */
function AppOnboardInit(props: { children: ReactNode }): ReactElement {
    const { children } = props;
    const { company } = useNullableCompanyContext();

    if (company) {
        return <Navigate to={`/company/${company.id}`} replace />;
    }

    return <>{children}</>;
}

/** Everything below needs a selected company; without one, go and make one. */
function AppCompanyInit(props: { children: ReactNode }): ReactElement {
    const { children } = props;
    const { company } = useNullableCompanyContext();
    const { companies } = useCompanies();

    if (!company) {
        return <Navigate to="/onboard" replace />;
    }

    // Before the app renders, not over the top of it: a suspended company's
    // data should not reach the page at all.
    if (company.suspended) {
        return <CompanySuspendedMask canSwitchCompany={companies.length > 1} />;
    }

    return (
        <CompanyContextProvider company={company}>
            {children}
        </CompanyContextProvider>
    );
}

/** Keeps the company in the URL and the company in context in step. */
function AppCompanyRedirect(): ReactElement {
    const { companiesMap } = useCompanies();

    // url of company this user is trying to access
    const { companyId } = useParams();

    // The in-mem company that's selected
    const { company } = useCompanyContext();
    const { setCompany } = useNullableCompanyContext();

    // If no companyId founding in URL, or the companyId does not exist for this user,
    // redirect to the company/:companyId page (Using the selected company context id as the companyId)
    if (!companyId || !companiesMap.get(companyId)) {
        return <Navigate to={`/company/${company.id}`} replace />;
    }

    // Here we know there is a companyId in the URL, and it exists for this user

    // Set the company context to match the one in the url
    if (companyId !== company.id) {
        setCompany(companyId);
    }

    return <Outlet />;
}

/** Shown while the app's opening reads are in flight. */
function AppLoading(): ReactElement {
    return (
        <div className={styles.container}>
            <UISpinner size={32} color={'white'} />
            <h2>We're loading your data...</h2>
        </div>
    );
}

/** Shown when one of them fails, with a way back. */
function AppLoadingError(props: { onRetryClick: () => void }): ReactElement {
    const { onRetryClick } = props;
    return (
        <div className={styles.container}>
            <h2>Something wrong happened</h2>
            <UIButton onClick={onRetryClick}>Retry</UIButton>
        </div>
    );
}

export default App;
